import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CREST } from "@/constants/testIds";

const COURSE_BG =
  "/brand/proshop-floor.jpg";

const SIZING_FIELDS = [
  { key: "shirt", label: "Shirt" },
  { key: "hat", label: "Hat" },
  { key: "outerwear", label: "Outerwear" },
  { key: "shoe", label: "Shoe" },
  { key: "belt", label: "Belt" },
];

export default function CrestProfilePage() {
  const { user, setUser } = useAuth();
  const [options, setOptions] = useState({ styles: [], brands: [], categories: [], logos: [] });
  const [editing, setEditing] = useState(null); // 'mobile' | 'sizing' | 'styles' | 'brands' | 'categories' | 'logos'
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [brandOther, setBrandOther] = useState("");

  useEffect(() => {
    api.get("/auth/me/preferences/options").then(({ data }) => setOptions(data)).catch(() => {});
  }, []);

  if (!user || user.role !== "member") return null;

  const startEdit = (section) => {
    setEditing(section);
    setFeedback(null);
    setBrandOther("");
    if (section === "mobile") setDraft({ mobile_number: user.mobile_number || user.phone || "" });
    if (section === "sizing") setDraft({ sizing: { ...(user.sizing || {}) } });
    if (section === "styles") setDraft({ style_preferences: [...(user.style_preferences || [])] });
    if (section === "categories") setDraft({ favorite_categories: [...(user.favorite_categories || [])] });
    if (section === "brands") setDraft({ favorite_brands: [...(user.favorite_brands || [])] });
    if (section === "logos") setDraft({ logo_preferences: [...(user.logo_preferences || [])] });
  };

  const cancel = () => { setEditing(null); setDraft({}); setBrandOther(""); };

  const toggle = (listKey, value) =>
    setDraft((d) => {
      const list = d[listKey] || [];
      const i = list.findIndex((v) => v.toLowerCase() === value.toLowerCase());
      return {
        ...d,
        [listKey]: i >= 0 ? list.filter((_, idx) => idx !== i) : [...list, value],
      };
    });

  const addBrandOther = () => {
    const v = brandOther.trim();
    if (!v) return;
    setDraft((d) => {
      const list = d.favorite_brands || [];
      if (list.some((b) => b.toLowerCase() === v.toLowerCase())) return d;
      return { ...d, favorite_brands: [...list, v] };
    });
    setBrandOther("");
  };

  const save = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      const { data } = await api.patch("/auth/me/preferences", { ...draft, mark_onboarded: true });
      setUser(data);
      setEditing(null);
      setDraft({});
      setFeedback({ type: "success", text: "Crest Profile updated." });
      setTimeout(() => setFeedback(null), 2500);
    } catch (err) {
      setFeedback({ type: "error", text: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally {
      setBusy(false);
    }
  };

  const sizingPretty = SIZING_FIELDS
    .map((f) => ({ ...f, value: (user.sizing || {})[f.key] }))
    .filter((f) => f.value);

  return (
    <div className="bg-cream min-h-screen" data-testid={CREST.page}>
      <section className="relative h-64 overflow-hidden border-b border-[#E5E3D8]">
        <img src={COURSE_BG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1A422D]/70" />
        <div className="absolute inset-0 flex items-end max-w-7xl mx-auto px-6 sm:px-10 py-8">
          <div className="text-[#F9F8F4]">
            <div className="overline" style={{ color: "#D9C68A" }}>Member</div>
            <h1 className="font-serif text-4xl sm:text-5xl mt-2 leading-none">Your Crest Profile</h1>
            <p className="mt-3 max-w-xl text-sm text-[#F9F8F4]/80">
              A private snapshot that helps the pro shop source the right things for you.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-6 sm:px-10 py-12 space-y-8">
        {feedback && (
          <div
            data-testid={CREST.saveFeedback}
            className={`px-4 py-3 text-sm border ${
              feedback.type === "success"
                ? "border-[#1A422D] bg-[#E9F0EB] text-[#1A422D]"
                : "border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]"
            }`}
          >
            {feedback.text}
          </div>
        )}

        {/* Mobile */}
        <Section title="Contact" subtitle="Mobile Number">
          {editing === "mobile" ? (
            <div className="space-y-3">
              <input
                type="tel"
                value={draft.mobile_number || ""}
                onChange={(e) => setDraft({ mobile_number: e.target.value })}
                data-testid={CREST.mobileInput}
                className="input-field"
                placeholder="(555) 123-4567"
              />
              <Actions onCancel={cancel} onSave={save} busy={busy} />
            </div>
          ) : (
            <ReadRow
              value={user.mobile_number || <span className="text-[#9A9A9A]">Not set</span>}
              onEdit={() => startEdit("mobile")}
              editTid={CREST.editMobile}
            />
          )}
        </Section>

        {/* Sizing */}
        <Section title="Sizing" subtitle="Kept private — never shared with vendors.">
          {editing === "sizing" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {SIZING_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="label-elegant block mb-2">{f.label}</label>
                  <input
                    type="text"
                    value={(draft.sizing || {})[f.key] || ""}
                    onChange={(e) => setDraft({ sizing: { ...(draft.sizing || {}), [f.key]: e.target.value } })}
                    data-testid={`${CREST.sizingPrefix}${f.key}`}
                    className="input-field"
                  />
                </div>
              ))}
              <div className="sm:col-span-2"><Actions onCancel={cancel} onSave={save} busy={busy} /></div>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-4">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 flex-1">
                {SIZING_FIELDS.map((f) => (
                  <div key={f.key}>
                    <dt className="label-elegant">{f.label}</dt>
                    <dd className="text-[#1A1A1A] mt-1">
                      {(user.sizing || {})[f.key] || <span className="text-[#9A9A9A]">—</span>}
                    </dd>
                  </div>
                ))}
              </dl>
              <button
                type="button"
                onClick={() => startEdit("sizing")}
                data-testid={CREST.editSizing}
                className="btn-secondary text-xs whitespace-nowrap"
              >
                Edit
              </button>
            </div>
          )}
        </Section>

        {/* Styles */}
        <Section title="Style Preferences" subtitle="Looks you gravitate toward.">
          {editing === "styles" ? (
            <div className="space-y-4">
              <ChipGrid
                options={options.styles}
                selected={draft.style_preferences}
                onToggle={(v) => toggle("style_preferences", v)}
                testIdPrefix={CREST.stylePrefix}
              />
              <Actions onCancel={cancel} onSave={save} busy={busy} />
            </div>
          ) : (
            <ChipsReadOnly
              items={user.style_preferences}
              onEdit={() => startEdit("styles")}
              editTid={CREST.editStyles}
            />
          )}
        </Section>

        {/* Categories */}
        <Section title="Favorite Categories" subtitle="What we should keep an eye out for.">
          {editing === "categories" ? (
            <div className="space-y-4">
              <ChipGrid
                options={options.categories}
                selected={draft.favorite_categories}
                onToggle={(v) => toggle("favorite_categories", v)}
                testIdPrefix={CREST.categoryPrefix}
              />
              <Actions onCancel={cancel} onSave={save} busy={busy} />
            </div>
          ) : (
            <ChipsReadOnly
              items={user.favorite_categories}
              onEdit={() => startEdit("categories")}
              editTid={CREST.editCategories}
            />
          )}
        </Section>

        {/* Brands */}
        <Section title="Favorite Brands" subtitle="Makers we should source from first.">
          {editing === "brands" ? (
            <div className="space-y-4">
              <ChipGrid
                options={options.brands}
                selected={draft.favorite_brands}
                onToggle={(v) => toggle("favorite_brands", v)}
                testIdPrefix={CREST.brandPrefix}
              />
              <div className="pt-4 border-t border-[#E5E3D8]">
                <label className="label-elegant block mb-2">Add Another Brand</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={brandOther}
                    onChange={(e) => setBrandOther(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBrandOther(); } }}
                    placeholder="e.g. Burberry"
                    data-testid={CREST.brandOtherInput}
                    className="input-field flex-1"
                  />
                  <button type="button" onClick={addBrandOther} data-testid={CREST.brandOtherAdd} className="btn-secondary text-xs">
                    Add
                  </button>
                </div>
                {(draft.favorite_brands || []).filter((b) => !(options.brands || []).includes(b)).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(draft.favorite_brands || [])
                      .filter((b) => !(options.brands || []).includes(b))
                      .map((b) => (
                        <span key={b} className="inline-flex items-center gap-2 px-3 py-1 text-xs border border-[#1A422D] bg-[#E9F0EB] text-[#1A422D]">
                          {b}
                          <button
                            type="button"
                            onClick={() => toggle("favorite_brands", b)}
                            className="text-[#1A422D] hover:text-[#A33]"
                            aria-label={`Remove ${b}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                  </div>
                )}
              </div>
              <Actions onCancel={cancel} onSave={save} busy={busy} />
            </div>
          ) : (
            <ChipsReadOnly
              items={user.favorite_brands}
              onEdit={() => startEdit("brands")}
              editTid={CREST.editBrands}
            />
          )}
        </Section>

        {/* Logo Preferences */}
        <Section
          title="Logo"
          subtitle="What we should put on the piece when we source it for you."
        >
          {editing === "logos" ? (
            <div className="space-y-4">
              <LogoChipGrid
                options={options.logos}
                selected={draft.logo_preferences}
                onToggle={(v) => toggle("logo_preferences", v)}
              />
              <Actions onCancel={cancel} onSave={save} busy={busy} />
            </div>
          ) : (
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                {(user.logo_preferences || []).length === 0 ? (
                  <p className="text-[#9A9A9A] text-sm">
                    Not set yet — tell us how you&rsquo;d like pieces finished.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {user.logo_preferences.map((b) => (
                      <span
                        key={b}
                        className="px-3 py-1 text-xs border border-[#E5E3D8] bg-white text-[#1A422D]"
                      >
                        {b}
                      </span>
                    ))}
                  </div>
                )}
                {/* Always tease the upcoming Members Only Logo. */}
                <div
                  className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] border border-[#B59A5A] bg-[#FBF6E8] text-[#8a6f33]"
                  data-testid="crest-members-only-logo-coming-soon"
                >
                  <span className="w-1.5 h-1.5 bg-[#B59A5A] rounded-full" />
                  Members Only Logo · Coming Soon
                </div>
              </div>
              <button
                type="button"
                onClick={() => startEdit("logos")}
                data-testid={CREST.editLogos}
                className="btn-secondary text-xs whitespace-nowrap"
              >
                Edit
              </button>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="surface-card p-8">
      <div className="overline">Crest</div>
      <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">{title}</h2>
      {subtitle && <p className="text-sm text-[#4A4A4A] mt-1">{subtitle}</p>}
      <div className="mt-6">{children}</div>
    </div>
  );
}

function ReadRow({ value, onEdit, editTid }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="text-[#1A1A1A]">{value}</div>
      <button type="button" onClick={onEdit} data-testid={editTid} className="btn-secondary text-xs">Edit</button>
    </div>
  );
}

function ChipsReadOnly({ items, onEdit, editTid }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        {(items || []).length === 0 ? (
          <p className="text-[#9A9A9A] text-sm">Not set yet — tell us what you like.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {items.map((b) => (
              <span key={b} className="px-3 py-1 text-xs border border-[#E5E3D8] bg-white text-[#1A422D]">
                {b}
              </span>
            ))}
          </div>
        )}
      </div>
      <button type="button" onClick={onEdit} data-testid={editTid} className="btn-secondary text-xs whitespace-nowrap">
        Edit
      </button>
    </div>
  );
}

function ChipGrid({ options, selected, onToggle, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-2.5">
      {(options || []).map((opt) => {
        const isOn = (selected || []).some((v) => v.toLowerCase() === opt.toLowerCase());
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onToggle(opt)}
            data-testid={`${testIdPrefix}${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            aria-pressed={isOn}
            className={`px-4 py-2 text-sm border transition-colors ${
              isOn
                ? "border-[#1A422D] bg-[#1A422D] text-[#F9F8F4]"
                : "border-[#E5E3D8] bg-white text-[#1A1A1A] hover:border-[#1A422D] hover:text-[#1A422D]"
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function Actions({ onCancel, onSave, busy }) {
  return (
    <div className="flex items-center justify-end gap-3">
      <button type="button" onClick={onCancel} className="btn-secondary text-xs">Cancel</button>
      <button type="button" onClick={onSave} disabled={busy} className="btn-primary">
        {busy ? "Saving…" : "Save"}
      </button>
    </div>
  );
}

/**
 * Logo chips honour an `available` flag. Disabled options render with a
 * "Coming Soon" badge and are non-clickable — the server also rejects
 * any submission of unavailable values, so this is purely a UX nicety.
 */
function LogoChipGrid({ options, selected, onToggle }) {
  const list = options || [];
  if (list.length === 0) {
    return <p className="text-[#9A9A9A] text-sm">No logo options available right now.</p>;
  }
  return (
    <div className="flex flex-wrap gap-2.5">
      {list.map((opt) => {
        const value = opt.value;
        const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, "-");
        const isOn = (selected || []).some((v) => v.toLowerCase() === value.toLowerCase());
        if (!opt.available) {
          return (
            <span
              key={value}
              data-testid={`crest-logo-coming-soon-${slug}`}
              title={opt.hint || "Coming soon"}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-dashed border-[#B59A5A] bg-[#FBF6E8] text-[#8a6f33] cursor-not-allowed"
            >
              <span className="w-1.5 h-1.5 bg-[#B59A5A] rounded-full" />
              {opt.label}
              <span className="text-[10px] uppercase tracking-[0.18em] ml-1">Coming Soon</span>
            </span>
          );
        }
        return (
          <button
            key={value}
            type="button"
            onClick={() => onToggle(value)}
            data-testid={`crest-logo-${slug}`}
            aria-pressed={isOn}
            title={opt.hint || ""}
            className={`px-4 py-2 text-sm border transition-colors ${
              isOn
                ? "border-[#1A422D] bg-[#1A422D] text-[#F9F8F4]"
                : "border-[#E5E3D8] bg-white text-[#1A1A1A] hover:border-[#1A422D] hover:text-[#1A422D]"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
